import React, { useState } from 'react';
import { useOrgCurrency } from '../../../hooks/useOrgCurrency.js';
import { Upload, X, FileSpreadsheet, AlertCircle, CheckCircle, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { apiRequest } from '../../../api/core.js';
import { downloadVehicleTemplate } from '../../../utils/importTemplates.js';

/**
 * Props:
 *  - skipApi: if true, skip the bulk-import server call entirely and hand the
 *    parsed vehicles back to the parent via onSuccess(parsedData). Used by the
 *    onboarding wizard so the vehicles become wizard state (to be persisted by
 *    generateFleet at the end of the flow), instead of being double-inserted
 *    via the standalone import endpoint.
 */
function VehicleImportModal({ isOpen, onClose, onSuccess, onMessage, skipApi = false }) {
  // Import dans la filiale active : sa devise, jamais EUR en dur.
  const { moneyScope } = useOrgCurrency();
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [importing, setImporting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [summary, setSummary] = useState(null);
  // Fichier loueur (Ayvens, Arval, Leaseplan, Athlon…) : lignes brutes + aperçu du serveur.
  // La reconnaissance appartient au backend (`detectLessorFile`) — on ne duplique pas ses alias ici.
  const [lessorRows, setLessorRows] = useState(null);
  const [lessorPreview, setLessorPreview] = useState(null);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
      onMessage && onMessage({
        title: 'Erreur',
        message: 'Veuillez sélectionner un fichier Excel (.xlsx ou .xls)',
        type: 'error'
      });
      return;
    }

    setFile(selectedFile);
    setErrors([]);
    setSummary(null);

    try {
      const data = await selectedFile.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });

      // Find the best sheet: "Etat de parc", "Export", or first sheet
      let sheetName = workbook.SheetNames.find(n =>
        n.toLowerCase().includes('etat') || n.toLowerCase().includes('parc')
      ) || workbook.SheetNames[0];

      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null });

      // Ligne d'en-tete : « Immatriculation » d'abord, sinon un en-tete de fichier
      // loueur (qui peut etre en anglais ou en polonais et ne jamais dire ce mot).
      const HEADER_HINTS = [
        'immatriculation', 'immat', 'plaque', 'registration', 'plate', 'kennzeichen', 'rejestracyjny',
        'loueur', 'bailleur', 'lessor', 'leasing', 'contrat', 'contract', 'umowy',
        'loyer', 'monthly', 'rata', 'vin', 'marque', 'make', 'marka',
      ];
      let headerRowIndex = -1;
      for (let i = 0; i < Math.min(jsonData.length, 20); i++) {
        const row = jsonData[i];
        if (!row) continue;
        const hits = row.filter(cell => cell && HEADER_HINTS.some(h => cell.toString().toLowerCase().includes(h))).length;
        if (hits >= 2) { headerRowIndex = i; break; }
        if (headerRowIndex === -1 && hits >= 1 && row.filter(c => c != null && c !== '').length >= 3) headerRowIndex = i;
      }

      if (headerRowIndex === -1) {
        throw new Error('Impossible de trouver la ligne d\'en-tête avec "Immatriculation"');
      }

      const headers = jsonData[headerRowIndex];
      const dataRows = jsonData.slice(headerRowIndex + 1).filter(row =>
        row && row.some(cell => cell !== null && cell !== '')
      );

      // Fichier loueur ? On demande au serveur (mode blanc : il n'ecrit rien).
      // La reconnaissance appartient au backend — on ne duplique pas ses alias ici.
      const rawRows = dataRows.map(row => Object.fromEntries(
        headers.map((h, i) => [String(h ?? `col${i}`), row[i] ?? null]).filter(([h]) => h && h !== 'null')
      ));
      if (!skipApi && rawRows.length > 0) {
        try {
          const dry = await apiRequest('/vehicles/bulk-import', {
            method: 'POST',
            body: JSON.stringify({ vehicles: rawRows, source: 'lessor', dry_run: true }),
          });
          setLessorRows(rawRows);
          setLessorPreview(dry.data || null);
          return;   // fichier loueur reconnu : son apercu remplace le parcours « etat de parc »
        } catch {
          // 400 « non reconnu comme fichier loueur » = parcours normal, rien a signaler.
        }
      }

      const vehicles = parseVehicles(headers, dataRows);
      setParsedData(vehicles);

    } catch (error) {
      console.error('Error parsing file:', error);
      onMessage && onMessage({
        title: 'Erreur',
        message: `Erreur lors de la lecture du fichier: ${error.message}`,
        type: 'error'
      });
    }
  };

  const parseVehicles = (headers, rows) => {
    const vehicles = [];
    const parseErrors = [];

    // Flexible column finder
    const col = (searchTerms) => {
      return headers.findIndex(h =>
        h && searchTerms.some(term => h.toString().toLowerCase().includes(term.toLowerCase()))
      );
    };
    const cell = (row, idx) => idx >= 0 && row[idx] != null ? row[idx].toString().trim() : null;
    const num = (row, idx) => { const v = cell(row, idx); return v ? parseFloat(v.replace(',', '.')) : null; };
    const isO = (row, idx) => cell(row, idx)?.toUpperCase() === 'O';

    // Vehicle columns
    const immatCol = col(['immatriculation', 'plaque']);
    const marqueCol = col(['marque', 'make']);
    const modeleCol = col(['appellation', 'modele', 'model']);
    const genreCol = col(['genre']);
    const carrosserieCol = col(['carrosserie']);
    const usageCol = col(['usage']);
    const energieCol = col(['energie', 'fuel', 'carburant']);
    const dateCirculationCol = col(['circulation', 'mise en service']);
    const puissanceCol = col(['puiss', 'puissance', 'power', 'cv']);
    const placesCol = col(['places']);
    const poidsCol = col(['poids']);
    const valAssCol = col(['val. assur', 'valeur assur']);
    const valNeufCol = col(['valeur à neuf', 'valeur neuf']);
    const modeAchatCol = col(['mode achat']);

    // Premium columns (AXA format)
    const tarifHTCol = col(['tarif ht']);
    const tarifTTCCol = col(['tarif ttc']);
    const dateEntreeCol = col(["date d'entr", 'date entr']);

    // Guarantee columns (AXA format: O/N + franchise)
    const gRC = col(['rc']); const gFR_RC = col(['fr rc']);
    const gDommage = col(['dommage']); const gFR_Dommage = col(['fr dommage']);
    const gIncendie = col(['incendie']); const gFR_Incendie = col(['fr incendie']);
    const gVol = col(['vol']); const gFR_Vol = col(['fr vol']);
    const gBDG = col(['bdg']); const gFR_BDG = col(['fr bdg']);
    const gAssistance = col(['assistance']);
    const gMarchandise = col(['marchandise']);
    const gPeinture = col(['peinture']);
    const gPerteFin = col(['perte financi']);
    const gPJ = col(['pj']);
    const gSecuCond = col(['sécu. conducteur', 'secu. conducteur', 'sécu conducteur']);
    const gVehRemp = col(['véh. remp', 'veh. remp', 'véh remp']);

    rows.forEach((row, index) => {
      try {
        const licencePlate = cell(row, immatCol);
        if (!licencePlate) {
          parseErrors.push({ row: index + 2, error: 'Immatriculation manquante' });
          return;
        }

        // Detect vehicle_category from genre
        const genre = cell(row, genreCol) || '';
        let vehicleCategory = 'VP';
        if (/utilitaire|fourgon|camion/i.test(genre)) vehicleCategory = 'VU';
        else if (/poids lourd|semi|remorque|tracteur/i.test(genre)) vehicleCategory = 'PL';
        else if (/engin|agricole|chantier/i.test(genre)) vehicleCategory = 'ENGIN';
        else if (/léger|berline|break|suv|monospace/i.test(genre)) vehicleCategory = 'VP';

        // Map fuel type
        const rawFuel = cell(row, energieCol) || '';
        const fuelMap = { 'diesel': 'diesel', 'gazole': 'diesel', 'go': 'diesel', 'essence': 'essence', 'sp': 'essence',
          'electri': 'electric', 'hybride': 'hybrid', 'gpl': 'gpl', 'gnv': 'gnv' };
        const fuelType = Object.entries(fuelMap).find(([k]) => rawFuel.toLowerCase().includes(k))?.[1] || rawFuel.toLowerCase();

        // Build guarantees array for insurance import
        const guarantees = [];
        if (isO(row, gRC)) guarantees.push({ code: 'RC_AUTO', franchise: num(row, gFR_RC) });
        if (isO(row, gDommage)) guarantees.push({ code: 'DOMMAGES_TOUS_ACCIDENTS', franchise: num(row, gFR_Dommage) });
        if (isO(row, gIncendie)) guarantees.push({ code: 'INCENDIE', franchise: num(row, gFR_Incendie) });
        if (isO(row, gVol)) guarantees.push({ code: 'VOL', franchise: num(row, gFR_Vol) });
        if (isO(row, gBDG)) guarantees.push({ code: 'BRIS_GLACE', franchise: num(row, gFR_BDG) });
        if (isO(row, gAssistance)) guarantees.push({ code: 'ASSISTANCE_0KM' });
        if (isO(row, gMarchandise)) guarantees.push({ code: 'MARCHANDISES' });
        if (isO(row, gPeinture)) guarantees.push({ code: 'PEINTURES_PUB' });
        if (isO(row, gPerteFin)) guarantees.push({ code: 'PERTES_FINANCIERES' });
        if (isO(row, gPJ)) guarantees.push({ code: 'PROTECTION_JURIDIQUE' });
        if (isO(row, gSecuCond)) guarantees.push({ code: 'PROTECTION_CONDUCTEUR' });
        if (isO(row, gVehRemp)) guarantees.push({ code: 'VEHICULE_REMPLACEMENT' });

        // Auto-detect coverage level from guarantees
        const hasDommage = guarantees.some(g => g.code === 'DOMMAGES_TOUS_ACCIDENTS');
        const hasVol = guarantees.some(g => g.code === 'VOL');
        const hasBDG = guarantees.some(g => g.code === 'BRIS_GLACE');
        let coverageLevel = 'tiers_simple';
        if (hasDommage && hasVol && hasBDG) coverageLevel = 'tous_risques';
        else if (hasVol || hasBDG) coverageLevel = 'tiers_plus';

        const vehicle = {
          license_plate: licencePlate,
          vin: `IMP${licencePlate.replace(/[^A-Z0-9]/gi, '')}${Date.now().toString(36).slice(-4).toUpperCase()}`,
          make: cell(row, marqueCol) || 'Unknown',
          model: cell(row, modeleCol) || 'Unknown',
          vehicle_category: vehicleCategory,
          fuel_type: fuelType,
          year: parseDateToYear(cell(row, dateCirculationCol)) || new Date().getFullYear(),
          power_hp: num(row, puissanceCol) ? parseInt(num(row, puissanceCol)) : null,
          seats: num(row, placesCol) ? parseInt(num(row, placesCol)) : null,
          status: 'active',
          // org_id is NOT set here: the server derives the target org from the
          // active organisation (X-Active-Org / tenant context), never from a
          // client-supplied value. See backend/middleware_layer.mjs POST
          // /api/v1/vehicles/bulk-import (payload.org_id is overwritten server-side).
          // Insurance data (used by bulk-import to create vehicle_insurance + coverage)
          _insurance: {
            premium_ht: num(row, tarifHTCol),
            premium_ttc: num(row, tarifTTCCol),
            coverage_level: coverageLevel,
            guarantees,
            entry_date: cell(row, dateEntreeCol),
            usage: cell(row, usageCol),
            body_type: cell(row, carrosserieCol),
            insured_value: num(row, valAssCol),
            new_value: num(row, valNeufCol),
            purchase_mode: cell(row, modeAchatCol),
          },
        };

        vehicles.push(vehicle);
      } catch (error) {
        parseErrors.push({ row: index + 2, error: error.message });
      }
    });

    setErrors(parseErrors);
    return vehicles;
  };

  const parseDateToYear = (dateValue) => {
    if (!dateValue) return null;

    // If it's a Date object (ExcelJS returns Date for date cells)
    if (dateValue instanceof Date) {
      return dateValue.getFullYear();
    }

    // If it's already a number, assume it's a year or Excel serial
    if (typeof dateValue === 'number') {
      if (dateValue > 1900 && dateValue < 2100) return dateValue;
      // Excel date serial number (days since 1900-01-01)
      if (dateValue > 25000 && dateValue < 60000) {
        const excelEpoch = new Date(1899, 11, 30); // Excel epoch
        const date = new Date(excelEpoch.getTime() + dateValue * 86400000);
        return date.getFullYear();
      }
    }

    // Try parsing as string
    if (typeof dateValue === 'string') {
      const match = dateValue.match(/(\d{4})/);
      if (match) return parseInt(match[1]);

      const parts = dateValue.split(/[\/\-]/);
      if (parts.length >= 3) {
        const year = parts.find(p => p.length === 4);
        if (year) return parseInt(year);
      }
    }

    return null;
  };

  /** Import d'un fichier loueur : les lignes brutes repartent telles quelles, le serveur mappe. */
  const handleLessorImport = async () => {
    if (!lessorRows || lessorRows.length === 0) return;
    setImporting(true);
    try {
      const res = await apiRequest('/vehicles/bulk-import', {
        method: 'POST',
        body: JSON.stringify({ vehicles: lessorRows, source: 'lessor' }),
      });
      const result = res.data || {};
      const touched = (result.created || 0) + (result.updated || 0);
      setSummary({
        total: lessorRows.length,
        success: touched,
        failures: result.skipped || 0,
        lessor: result,
        errors: (result.errors || []).map(e => ({ vehicle: e.plate, error: e.error })),
      });
      if (touched > 0) {
        onMessage?.({
          title: 'Succès',
          message: `${result.updated || 0} véhicule(s) mis à jour, ${result.created || 0} créé(s)`,
          type: (result.skipped || 0) === 0 ? 'success' : 'warning',
        });
        onSuccess?.(result.rows || []);
      } else {
        onMessage?.({ title: 'Erreur', message: 'Aucune ligne reprise', type: 'error' });
      }
    } catch (error) {
      setSummary({ total: lessorRows.length, success: 0, failures: lessorRows.length, errors: [{ vehicle: 'Import loueur', error: error.message }] });
      onMessage?.({ title: 'Erreur', message: `Erreur d'import: ${error.message}`, type: 'error' });
    }
    setImporting(false);
  };

  const handleImport = async () => {
    if (!parsedData || parsedData.length === 0) {
      onMessage && onMessage({
        title: 'Erreur',
        message: 'Aucun véhicule à importer',
        type: 'error'
      });
      return;
    }

    // Preview-only path (onboarding wizard): hand parsed rows back to the
    // parent so they become wizard state. The parent is responsible for the
    // eventual persistence (generateFleet at the end of the flow).
    if (skipApi) {
      onMessage?.({
        title: 'Import prêt',
        message: `${parsedData.length} véhicule(s) chargé(s) depuis le fichier`,
        type: 'success'
      });
      onSuccess?.(parsedData);
      return;
    }

    setImporting(true);

    try {
      const res = await apiRequest('/vehicles/bulk-import', {
        method: 'POST',
        body: JSON.stringify({ vehicles: parsedData }),
      });

      const result = res.data || {};
      const successCount = result.created || 0;
      const failureCount = result.skipped || 0;
      const importErrors = (result.errors || []).map(e => ({ vehicle: e.plate, error: e.error }));

      setSummary({ total: parsedData.length, success: successCount, failures: failureCount, errors: importErrors });

      if (successCount > 0) {
        onMessage?.({ title: 'Succès', message: `${successCount} véhicule(s) importé(s)${failureCount > 0 ? `, ${failureCount} échec(s)` : ''}`, type: failureCount === 0 ? 'success' : 'warning' });
        if (failureCount === 0) { onSuccess?.(parsedData); }
      } else {
        onMessage?.({ title: 'Erreur', message: 'Aucun véhicule importé', type: 'error' });
      }
    } catch (error) {
      setSummary({ total: parsedData.length, success: 0, failures: parsedData.length, errors: [{ vehicle: 'Bulk import', error: error.message }] });
      onMessage?.({ title: 'Erreur', message: `Erreur d'import: ${error.message}`, type: 'error' });
    }

    setImporting(false);
  };

  const handleReset = () => {
    setFile(null);
    setParsedData(null);
    setErrors([]);
    setSummary(null);
    setLessorRows(null);
    setLessorPreview(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="w-6 h-6" />
              <h2 className="text-2xl font-bold">Importer des Véhicules</h2>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Formats supportés :</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• <strong>Export AXA "État de parc"</strong> — import complet (véhicules + garanties + primes + franchises)</li>
                  <li>• <strong>Fichier loueur</strong> (Ayvens, Arval, Leaseplan, Athlon…) — met à jour les contrats : loyer, durée, km annuel, km au départ, prix du km excédentaire</li>
                  <li>• <strong>Excel générique</strong> — colonnes : Immatriculation (requis), Marque, Modèle, Genre, Énergie, Puissance</li>
                  <li>• Fichier .xlsx ou .xls, première feuille ou feuille "Etat de parc"</li>
                </ul>
                <p className="text-xs text-blue-600 mt-2">Le format est reconnu automatiquement. Les garanties (RC, Dommage, Vol, BDG...) et franchises sont détectées depuis le format assureur ; les montants d'un fichier loueur sont convertis dans la devise de la filiale.</p>
              </div>
              <button
                onClick={() => downloadVehicleTemplate()}
                className="shrink-0 px-4 py-2 bg-white border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-100 flex items-center gap-2 text-sm font-medium"
              >
                <Download className="w-4 h-4" />
                Télécharger le modèle
              </button>
            </div>
          </div>

          {/* Aperçu d'un fichier loueur — rien n'est encore écrit */}
          {lessorPreview && !summary && (
            <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-amber-600" />
                <h3 className="font-semibold text-amber-900">Fichier loueur reconnu — {lessorRows?.length || 0} ligne(s)</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="bg-white rounded p-2 border border-amber-200">
                  <p className="text-gray-500 text-xs">À mettre à jour</p>
                  <p className="text-lg font-bold text-amber-700">{lessorPreview.updated || 0}</p>
                </div>
                <div className="bg-white rounded p-2 border border-amber-200">
                  <p className="text-gray-500 text-xs">À créer</p>
                  <p className="text-lg font-bold text-green-700">{lessorPreview.created || 0}</p>
                </div>
                <div className="bg-white rounded p-2 border border-amber-200">
                  <p className="text-gray-500 text-xs">Ignorées</p>
                  <p className="text-lg font-bold text-gray-700">{lessorPreview.skipped || 0}</p>
                </div>
                <div className="bg-white rounded p-2 border border-amber-200">
                  <p className="text-gray-500 text-xs">Devise de la filiale</p>
                  <p className="text-lg font-bold text-gray-900">{lessorPreview.currency || '—'}</p>
                </div>
              </div>
              {lessorPreview.converted > 0 && (
                <p className="text-xs text-amber-800">
                  {lessorPreview.converted} ligne(s) converties vers {lessorPreview.currency} depuis la devise du fichier.
                </p>
              )}
              {(lessorPreview.errors || []).length > 0 && (
                <div className="max-h-40 overflow-y-auto text-xs text-red-700 space-y-1">
                  {(lessorPreview.errors || []).slice(0, 20).map((e, i) => (
                    <p key={i}>• Ligne {e.row} {e.plate ? `(${e.plate})` : ''} — {e.error}</p>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-600">
                Colonnes reconnues : {Object.keys(lessorPreview.columns || {}).join(', ') || '—'}
              </p>
            </div>
          )}

          {/* File Upload */}
          {!parsedData && !summary && !lessorPreview && (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
              <div className="text-center">
                <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <label className="cursor-pointer">
                  <span className="text-blue-600 hover:text-blue-700 font-medium">
                    Cliquez pour sélectionner un fichier
                  </span>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>
                <p className="text-sm text-gray-500 mt-2">ou glissez-déposez un fichier Excel ici</p>
              </div>
            </div>
          )}

          {/* Preview */}
          {parsedData && !summary && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-900">
                  Aperçu: {parsedData.length} véhicule(s) trouvé(s)
                </h3>
                <button
                  onClick={handleReset}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  Changer de fichier
                </button>
              </div>

              {errors.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-900">{errors.length} erreur(s) de parsing</p>
                      <ul className="text-sm text-amber-800 mt-2 space-y-1 max-h-32 overflow-y-auto">
                        {errors.slice(0, 10).map((err, i) => (
                          <li key={i}>Ligne {err.row}: {err.error}</li>
                        ))}
                        {errors.length > 10 && (
                          <li className="font-medium">... et {errors.length - 10} autre(s)</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-96">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Immatriculation</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Véhicule</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Énergie</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Couverture</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Prime TTC</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Garanties</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {parsedData.slice(0, 50).map((vehicle, i) => {
                        const ins = vehicle._insurance || {};
                        const catColors = { VP: 'bg-sky-100 text-sky-700', VU: 'bg-indigo-100 text-indigo-700', PL: 'bg-amber-100 text-amber-700', ENGIN: 'bg-orange-100 text-orange-700' };
                        const covColors = { tous_risques: 'bg-blue-100 text-blue-700', tiers_plus: 'bg-gray-100 text-gray-700', tiers_simple: 'bg-slate-100 text-slate-600' };
                        const covLabels = { tous_risques: 'Tous Risques', tiers_plus: 'Tiers+', tiers_simple: 'Tiers' };
                        return (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-sm font-mono font-medium text-gray-900">{vehicle.license_plate}</td>
                            <td className="px-3 py-2 text-sm text-gray-600">{vehicle.make} {vehicle.model}</td>
                            <td className="px-3 py-2 text-center">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${catColors[vehicle.vehicle_category] || 'bg-gray-100 text-gray-600'}`}>
                                {vehicle.vehicle_category}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-600">{vehicle.fuel_type || '-'}</td>
                            <td className="px-3 py-2 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${covColors[ins.coverage_level] || 'bg-gray-100'}`}>
                                {covLabels[ins.coverage_level] || ins.coverage_level || '-'}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-sm text-right text-gray-700">
                              {ins.premium_ttc ? moneyScope(ins.premium_ttc) : '-'}
                            </td>
                            <td className="px-3 py-2 text-center text-xs text-gray-500">
                              {ins.guarantees?.length || 0}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {parsedData.length > 50 && (
                  <div className="bg-gray-50 px-4 py-2 text-sm text-gray-600 text-center">
                    ... et {parsedData.length - 50} véhicule(s) supplémentaire(s)
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Summary */}
          {summary && (
            <div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-green-900 text-lg">Import terminé</h3>
                    <div className="mt-2 space-y-1 text-sm text-green-800">
                      <p>Total: {summary.total} {summary.lessor ? 'ligne(s)' : 'véhicule(s)'}</p>
                      {summary.lessor ? (
                        <>
                          <p>✓ Mis à jour: {summary.lessor.updated || 0}</p>
                          <p>✓ Créés: {summary.lessor.created || 0}</p>
                        </>
                      ) : (
                        <p>✓ Succès: {summary.success}</p>
                      )}
                      {summary.failures > 0 && <p>✗ Échecs: {summary.failures}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {summary.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="font-medium text-red-900 mb-2">Erreurs d'import:</p>
                  <ul className="text-sm text-red-800 space-y-1 max-h-48 overflow-y-auto">
                    {summary.errors.map((err, i) => (
                      <li key={i}>{err.vehicle}: {err.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
          >
            {summary ? 'Fermer' : 'Annuler'}
          </button>
          {parsedData && !summary && (
            <button
              onClick={handleImport}
              disabled={importing || parsedData.length === 0}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Upload className="w-5 h-5" />
              {importing ? 'Import en cours...' : `Importer ${parsedData.length} véhicule(s)`}
            </button>
          )}
          {lessorPreview && !summary && (
            <button
              onClick={handleLessorImport}
              disabled={importing || ((lessorPreview.created || 0) + (lessorPreview.updated || 0)) === 0}
              className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Upload className="w-5 h-5" />
              {importing
                ? 'Import en cours...'
                : `Appliquer : ${lessorPreview.updated || 0} mise(s) à jour, ${lessorPreview.created || 0} création(s)`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default VehicleImportModal;
